import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaCommentDots, FaArrowRight, FaArrowLeft, FaCog, FaPalette,
  FaCode, FaEye, FaClock, FaChevronRight, FaCheckCircle,
  FaExclamationTriangle, FaLightbulb, FaToggleOn, FaComments
} from "react-icons/fa";

export default function WhatsAppWidgetGuide() {
  useEffect(() => {
    document.title = "WhatsApp Widget Guide | Walytic - Add a Chat Widget to Your Website";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="WhatsApp Widget Guide" subtitle="Add a floating WhatsApp chat button to your website -- visitors click it and start a conversation with you." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'configuration', label: 'Widget configuration options' },
              { id: 'quick-replies', label: 'Setting up quick replies' },
              { id: 'live-preview', label: 'Live preview' },
              { id: 'generate-code', label: 'Save and generate embed code' },
              { id: 'install', label: 'Installing on your website' },
              { id: 'tips', label: 'Tips for better engagement' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Configuration */}
        <section id="configuration" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Widget configuration options</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Open <strong>WhatsApp Widget</strong> from the sidebar. The left panel has all the settings,
            and the right panel shows a live preview that updates as you change values.
          </p>

          <div className="space-y-4">
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaComments className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Phone Number</h3>
              </div>
              <p className="text-ink-secondary">
                Your WhatsApp number with country code. Do not include the <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">+</code> sign.
                Example: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">919876543210</code> for India,
                {' '}<code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">14155551234</code> for US.
              </p>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaComments className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Welcome Message</h3>
              </div>
              <p className="text-ink-secondary">
                The text shown in the popup when a visitor opens the widget. Something like
                &quot;Hi! How can we help you today?&quot; works well. Keep it short -- two lines max.
              </p>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FaPalette className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Brand Color</h3>
              </div>
              <p className="text-ink-secondary">
                Pick a color using the color picker, or type a hex code directly.
                Default is <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">#25D366</code> (WhatsApp green).
                Match this to your website&apos;s primary brand color for a cohesive look.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-surface-card rounded-xl border border-surface-border p-6">
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Position</h3>
                <p className="text-ink-secondary text-sm">
                  Choose <strong>Left</strong> or <strong>Right</strong>. Most websites place it on the bottom-right,
                  but if you have another widget there (like Intercom), put it on the left.
                </p>
              </div>
              <div className="bg-surface-card rounded-xl border border-surface-border p-6">
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Avatar URL</h3>
                <p className="text-ink-secondary text-sm">
                  A URL to an image shown as the agent&apos;s profile picture in the widget header.
                  Use your logo or a team photo. Square images (e.g., 200x200px) work best.
                </p>
              </div>
              <div className="bg-surface-card rounded-xl border border-surface-border p-6">
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Bubble Text</h3>
                <p className="text-ink-secondary text-sm">
                  A short tooltip that appears next to the floating button. Example: &quot;Chat with us&quot;.
                  Leave it empty to show just the icon.
                </p>
              </div>
              <div className="bg-surface-card rounded-xl border border-surface-border p-6">
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Business Hours Message</h3>
                <p className="text-ink-secondary text-sm">
                  An alternate message displayed outside your business hours. Example:
                  &quot;We are away right now. Leave a message and we will reply by morning.&quot;
                </p>
              </div>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaToggleOn className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Active Toggle</h3>
              </div>
              <p className="text-ink-secondary">
                Flip this off to disable the widget without removing the embed code from your site.
                The script stays on the page but renders nothing. Flip it back on when you are ready.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Quick Replies */}
        <section id="quick-replies" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Setting up quick replies</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Quick replies are pre-written buttons that appear below the welcome message. When a visitor
            clicks one, it auto-fills as their first message to you on WhatsApp.
          </p>

          <div className="bg-surface-card rounded-xl border border-surface-border p-6 mb-6">
            <p className="text-sm font-medium text-ink-primary mb-3">Rules:</p>
            <ul className="space-y-2 text-ink-secondary text-sm">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Up to <strong>5 quick replies</strong> per widget</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Maximum <strong>50 characters</strong> each</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>They appear as pill-shaped buttons in the popup</span>
              </li>
            </ul>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm font-medium text-ink-primary mb-2">Good quick reply examples:</p>
            <div className="flex flex-wrap gap-2">
              {['I want a demo', 'Pricing info', 'Track my order', 'Talk to support'].map((reply) => (
                <span key={reply} className="bg-surface-card border border-surface-border rounded-full px-3 py-1.5 text-sm text-ink-primary">
                  {reply}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Section: Live Preview */}
        <section id="live-preview" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Live preview</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            The right side of the configuration page shows a real-time preview of your widget.
            Every change you make on the left -- color, message, quick replies, avatar --
            reflects instantly in the preview.
          </p>
          <p className="text-ink-secondary leading-relaxed">
            The preview shows both states: the floating button (collapsed) and the chat popup (expanded).
            Click the preview button to toggle between them. What you see here is exactly what your
            website visitors will see.
          </p>
        </section>

        {/* Section: Generate Code */}
        <section id="generate-code" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Save and generate embed code</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Once you are happy with the preview, click <strong>&quot;Save &amp; Generate Code&quot;</strong>.
            Walytic saves your configuration and produces a script tag.
          </p>

          <div className="bg-slate-900 rounded-xl p-6 mb-6">
            <p className="text-xs text-slate-400 mb-3 font-mono">Embed code (example)</p>
            <code className="text-sm text-green-400 leading-relaxed block">
              {'<script src="https://cdn.walytic.com/widget/YOUR_ID.js" defer></script>'}
            </code>
          </div>

          <p className="text-ink-secondary">
            Copy this snippet. You only need to paste it once -- after that, any changes you make
            in Walytic (new message, different color, toggling active/inactive) take effect
            automatically without touching your website code again.
          </p>
        </section>

        {/* Section: Install */}
        <section id="install" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Installing on your website</h2>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open your website&apos;s HTML</h3>
                <p className="text-ink-secondary">
                  Find the main HTML file or template that contains your <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">{'</body>'}</code> closing tag.
                  In WordPress this is usually <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">footer.php</code>.
                  In Next.js it is your root layout. In Shopify, check <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">theme.liquid</code>.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Paste the script tag</h3>
                <p className="text-ink-secondary">
                  Place the script tag just before the closing <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">{'</body>'}</code> tag.
                  The <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">defer</code> attribute means it loads without
                  blocking your page, so it will not affect site speed.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Deploy and test</h3>
                <p className="text-ink-secondary">
                  Deploy your site changes and visit any page. The WhatsApp button should appear in the
                  corner you selected. Click it to verify the popup opens with your welcome message and quick replies.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
            <div className="flex items-start gap-2">
              <FaExclamationTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                If the widget does not appear, check your browser console for errors. The most common
                issue is an ad blocker or Content Security Policy (CSP) blocking the script.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips for better engagement</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Match your brand color', text: 'A widget that blends with your site looks intentional. A default green button on a blue website looks tacked on.' },
              { title: 'Keep the welcome message short', text: 'One or two sentences max. Visitors want to ask a question, not read a paragraph.' },
              { title: 'Add 2-3 quick replies', text: 'Quick replies reduce friction. Pick your top customer questions: pricing, demo, support.' },
              { title: 'Set a business hours message', text: 'Telling visitors when to expect a reply prevents frustration. Nobody likes sending a message into the void.' },
              { title: 'Test on mobile', text: 'Most WhatsApp traffic comes from mobile devices. Make sure the widget is tappable and does not overlap other elements.' },
              { title: 'Use the Active toggle', text: 'Disable the widget during downtime or maintenance without editing your site code. Turn it back on with one click.' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev / Next */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/image-to-url" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Image to URL
          </Link>
          <Link to="/guide" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            All Guides <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
